import React, { useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  Pagination,
  Snackbar,
  Alert,
  Typography,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  InfoOutlined,
  Add as AddIcon,
  AutoAwesome as AIIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import enUS from 'antd/locale/en_US';
import itIT from 'antd/locale/it_IT';
import { colors } from '../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../themes/responsive';
import FilterPills from '../ExperimentsTable/FilterPills';
import { theme, ThemedTypography } from '../../themes/technicalAlerts';
import { createProblemsColumns, getAggregatedColumns } from '../../themes/problems/columns';
import KPIBox from '../shared/KPIBox';
import { TrendingDown, Assignment } from '@mui/icons-material';
import { useProblemsState } from '../../hooks/useProblemsState';
import { useProblemsFilters } from '../../hooks/useProblemsFilters';
import { useAggregatedProblemsData } from '../../hooks/useAggregatedProblemsData';
import { getFilterValues } from './filterConfig';
import ProblemAnalysisPanel from '../ProblemAnalysisPanel/ProblemAnalysisPanel';
import AcceptAllConfirmModal from './AcceptAllConfirmModal';
import ExpandedExperimentsTable from './ExpandedExperimentsTable';
import getExpandedRowColumns from './expandedRowColumns';
import { bulkAcceptExperiments, getProblemsGroupDetails, inlineUpdateExperiment } from '../../data/problemsApi';
import { getApiErrorMessage } from '../../utils/apiError';
import AppliedPricingDialog from '../shared/AppliedPricingDialog';
import RejectReasonDialog from '../RejectReasonDialog/RejectReasonDialog';
import { useRejectionModal } from '../../hooks/useRejectionModal';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { useAuth } from '../../context/AuthContext';
import 'antd/dist/reset.css';

// La vista unitaria es la subtabla del desplegable agrupado sin agrupar: sus filas viven en
// expandedRowData bajo esta clave, así aceptar/rechazar/IA usan los mismos handlers.
const UNIT_KEY = 'unit';
const UNIT_DEFAULT_SORT = { field: 'estimatedLoss', order: 'descend' };

const ProblemsTable = ({
  viewMode = 'group',
  onViewModeChange = () => {},
  onFiltersChange = () => {},
  onTotalsChange = () => {},
  showToast: externalShowToast
}) => {
  const { t, i18n } = useTranslation();
  const locale = { es: esES, en: enUS, it: itIT }[i18n.language] || esES;
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Local filter state management (like ExperimentsTable)
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({ field: 'criticity', order: 'descend' });
  const [unitSorter, setUnitSorter] = useState(UNIT_DEFAULT_SORT);

  // Accept All modal state
  const [acceptAllModalOpen, setAcceptAllModalOpen] = useState(false);
  const [pendingAcceptAll, setPendingAcceptAll] = useState(null);
  const [acceptAllLoading, setAcceptAllLoading] = useState(false);

  // Expandable row state
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  // Las filas abiertas traen sus textos ya traducidos por el backend (Accept-Language) y no se
  // vuelven a pedir solas: al cambiar de idioma se cierran, y al abrirlas llegan en el nuevo.
  const languageRef = React.useRef(i18n.language);
  React.useEffect(() => {
    if (languageRef.current === i18n.language) return;
    languageRef.current = i18n.language;
    setExpandedRowKeys([]);
  }, [i18n.language]);
  const [expandedRowData, setExpandedRowData] = useState({});
  const [expandedRowLoading, setExpandedRowLoading] = useState({});
  const [expandedRowSorting, setExpandedRowSorting] = useState({});

  // Draft state for row-level pending changes
  const [experimentDrafts, setExperimentDrafts] = useState({});
  const [priceReview, setPriceReview] = useState(null);

  // Ref to track self-triggered updates (prevents refetch after own accept/reject)
  const selfTriggeredUpdate = useRef(false);

  // Ref to track which experiment opened the AI panel (for optimistic updates)
  // Using ref instead of state to avoid closure issues in callbacks
  const aiPanelContextRef = useRef(null);

  // Centralized state management (without filters)
  const state = useProblemsState({
    onFiltersChange,
    externalShowToast,
    initialFilters: {} // No default filters for problems
  });

  // Hook for dynamic filter values (faceted search) - use local filteredInfo
  const { filterData, loading: filtersLoading, error: filtersError } = useProblemsFilters(filteredInfo);

  // Hook for table data with server-side operations - use local filteredInfo and viewMode
  const {
    data,
    loading,
    error: tableError,
    totals,
    paginationInfo,
    setPage,
    refetch,
    refetchKPIs,
    updateRowData,
    removeRow
  } = useAggregatedProblemsData(viewMode, filteredInfo, {
    pageSize: 10,
    initialPage: 1,
    sorter: viewMode === 'unit' ? unitSorter : sortedInfo
  });

  // Use centralized toast management
  const { showToast } = state.toast;

  // Pass totals to parent when available
  React.useEffect(() => {
    onTotalsChange(totals);
  }, [totals, onTotalsChange]);

  // Use centralized toast close handler
  const { handleToastClose } = state.toast;

  // Show error toast if table data loading fails
  React.useEffect(() => {
    if (tableError) {
      showToast(t('error_loading_problems_data'), 'error');
    }
  }, [tableError, showToast, t]);

  // Listen for experiment updates from other components (like AI sidebar)
  React.useEffect(() => {
    const handleExperimentsUpdated = (event) => {
      // Skip refetch if this component triggered the update (optimistic update already done)
      if (selfTriggeredUpdate.current) {
        selfTriggeredUpdate.current = false;
        return;
      }
      // Only refetch for external updates (e.g., AI sidebar)
      setExpandedRowData({});
      refetch();
    };

    window.addEventListener('experimentsUpdated', handleExperimentsUpdated);

    return () => {
      window.removeEventListener('experimentsUpdated', handleExperimentsUpdated);
    };
  }, [refetch]);

  // Use server-provided filter values only - no fallback to page data
  const filterValues = useMemo(() => getFilterValues(filterData), [filterData]);

  // Handler for opening AI analysis panel
  // La fila unitaria pasa el id del problema a secas (columns.js llama
  // onOpenAIAnalysisPanel(record.id)); la vista agrupada pasa el record del experimento con
  // problemId. Sin aceptar ambas formas, el panel recibía problemId=undefined desde la fila
  // unitaria y se quedaba girando para siempre sin disparar ningún fetch.
  const handleOpenAIAnalysisPanel = useCallback((record, groupingKey) => {
    const clientId = currentUser?.customer_id;

    if (!clientId) {
      showToast('Client ID not found for current user', 'error');
      return;
    }

    const isRecord = record !== null && typeof record === 'object';
    const resolvedProblemId = isRecord ? (record.problemId ?? record.id) : record;

    if (!resolvedProblemId) {
      showToast('Problem not found for this row', 'error');
      return;
    }

    // Store context for optimistic update when experiment is created/accepted
    aiPanelContextRef.current = {
      experimentId: isRecord ? record.experimentId : undefined,
      groupingKey,
      opportunityCost: (isRecord ? record.opportunityCost : 0) || 0
    };

    // Set the sidebar content with overlay mode
    window.currentPageSidebar = {
      content: (
        <ProblemAnalysisPanel
          problemId={resolvedProblemId}
          clientId={clientId}
          onClose={() => {
            window.currentPageSidebar = null;
            aiPanelContextRef.current = null; // Clear context when panel closes
            window.dispatchEvent(new Event('sidebarUpdate'));
          }}
          onDataRefresh={() => {
            // Do optimistic update like accept/reject instead of full refetch
            const ctx = aiPanelContextRef.current;
            if (ctx && ctx.groupingKey && ctx.groupingKey !== UNIT_KEY && ctx.experimentId) {
              // Calculate remaining experiments and update both states atomically
              setExpandedRowData(prev => {
                const currentExperiments = prev[ctx.groupingKey] || [];
                const remainingExperiments = currentExperiments.filter(
                  exp => exp.experimentId !== ctx.experimentId
                );

                // Use queueMicrotask to update main table AFTER this state update commits
                queueMicrotask(() => {
                  if (remainingExperiments.length === 0) {
                    // No more experiments - collapse and remove from main table
                    setExpandedRowKeys(keys => keys.filter(key => key !== ctx.groupingKey));
                    removeRow(ctx.groupingKey);
                  } else {
                    // Get current parent row to calculate new total
                    const parentRow = data.find(row => row.groupingKey === ctx.groupingKey);
                    const newTotalCost = (parentRow?.totalOpportunityCost || 0) - ctx.opportunityCost;

                    // Update main table count and opportunity cost (without repositioning)
                    updateRowData(ctx.groupingKey, {
                      experimentCount: remainingExperiments.length,
                      experimentIds: remainingExperiments.map(exp => exp.experimentId),
                      totalOpportunityCost: Math.max(0, newTotalCost) // Prevent negative values
                    });
                  }
                });

                return {
                  ...prev,
                  [ctx.groupingKey]: remainingExperiments
                };
              });

              // Mark as self-triggered to prevent refetch in event handler
              selfTriggeredUpdate.current = true;

              // Emit event for other components that might need to know
              window.dispatchEvent(new CustomEvent('experimentsUpdated', {
                detail: { experimentId: ctx.experimentId, source: 'aiPanel' }
              }));
            } else {
              // Fallback to refetch if context is missing
              refetch();
            }

            // Refetch KPIs after experiment created via AI panel. La vista unitaria no: su refetch
            // ya trae los KPIs con los filtros aplicados y estos, sin filtros, los pisarían.
            if (ctx?.groupingKey !== UNIT_KEY) {
              refetchKPIs();
            }

            showToast(t('experimentCreatedSuccessfully') || 'Experiment created successfully!', 'success');
            aiPanelContextRef.current = null; // Clear context after use
          }}
          t={t}
        />
      ),
      overlayMode: true
    };

    // Notify Layout to show sidebar
    window.dispatchEvent(new Event('sidebarUpdate'));
  }, [currentUser?.customer_id, data, refetch, refetchKPIs, showToast, t, updateRowData, removeRow]);

  // Handler for Accept All action - opens confirmation modal
  const handleAcceptAll = useCallback((experimentIds, groupLabel) => {
    setPendingAcceptAll({
      experimentIds,
      groupLabel,
      experimentCount: experimentIds.length
    });
    setAcceptAllModalOpen(true);
  }, []);

  // Confirm Accept All action - calls API and refetches data
  const confirmAcceptAll = useCallback(async () => {
    if (!pendingAcceptAll || !currentUser?.customer_id) {
      return;
    }

    setAcceptAllLoading(true);

    try {
      // Collect experiments with fallback prices from expanded row data (if available)
      const experimentsWithPrices = [];
      for (const experimentId of pendingAcceptAll.experimentIds) {
        // Search for this experiment in all expanded row data
        for (const experiments of Object.values(expandedRowData)) {
          const exp = experiments.find(e => e.experimentId === experimentId);
          if (exp?.isFallbackPrice && exp?.originalPrice) {
            experimentsWithPrices.push({
              experimentId: exp.experimentId,
              originalPrice: exp.originalPrice,
              isFallbackPrice: true
            });
            break;
          }
        }
      }

      // Lo elegido en el desplegable de cada fila se acepta con ella, igual que al aceptar una a una
      const experimentProductUpdates = [];
      for (const experimentId of pendingAcceptAll.experimentIds) {
        const draft = experimentDrafts[experimentId];
        if (!draft?.candidateProductId && draft?.discount === undefined) continue;
        const exp = Object.values(expandedRowData).flat().find(e => e.experimentId === experimentId);
        const candidateProductId = draft.candidateProductId || exp?.candidateProductIds?.[0];
        if (candidateProductId) {
          experimentProductUpdates.push({ experimentId, candidateProductId, discount: draft.discount ?? exp?.discount });
        }
      }

      const result = await bulkAcceptExperiments(
        currentUser.customer_id,
        pendingAcceptAll.experimentIds,
        experimentsWithPrices,
        experimentProductUpdates
      );
      setExperimentDrafts(prev => {
        const next = { ...prev };
        pendingAcceptAll.experimentIds.forEach(id => { delete next[id]; });
        return next;
      });

      // Show success message
      const successCount = result.successCount || pendingAcceptAll.experimentCount;
      showToast(
        t('experimentsAcceptedSuccessfully', {
          defaultValue: '{{count}} experiments accepted successfully',
          count: successCount
        }),
        'success'
      );

      // Close modal
      setAcceptAllModalOpen(false);
      setPendingAcceptAll(null);

      // Refresh table data
      refetch();
    } catch (error) {
      console.error('Error accepting experiments:', error);
      showToast(
        getApiErrorMessage(error, t('errorAcceptingExperiments', {
          defaultValue: 'Error accepting experiments. Please try again.'
        })),
        'error'
      );
    } finally {
      setAcceptAllLoading(false);
    }
  }, [pendingAcceptAll, currentUser?.customer_id, showToast, t, refetch, expandedRowData, experimentDrafts]);

  // Fetch expanded row details
  const fetchExpandedDetails = useCallback(async (record, forceRefresh = false, skipLoading = false, sortParams = null) => {
    const { groupingKey, experimentIds } = record;

    if (!experimentIds || experimentIds.length === 0) {
      return;
    }

    // Note: No cache check here - we always fetch when this function is called
    // The parent component handles caching by not calling this function unnecessarily

    // Set loading state (skip if sorting)
    if (!skipLoading) {
      setExpandedRowLoading(prev => ({
        ...prev,
        [groupingKey]: true
      }));
    }

    // Get sorting for this specific row (default to opportunityCost desc)
    // Use sortParams if provided, otherwise read from state
    const rowSorting = sortParams || expandedRowSorting[groupingKey] || { field: 'opportunityCost', order: 'descend' };
    const sortField = rowSorting.field || 'opportunityCost';
    const sortDirection = rowSorting.order === 'ascend' ? 'asc' : 'desc';

    try {
      const response = await getProblemsGroupDetails(
        currentUser?.customer_id,
        experimentIds,
        {
          sortField,
          sortDirection
        }
      );

      // Cache the data - response is already the array from the API
      setExpandedRowData(prev => ({
        ...prev,
        [groupingKey]: response || []
      }));
    } catch (error) {
      console.error('Error fetching expanded row details:', error);
      showToast(t('errorLoadingGroupDetails', {
        defaultValue: 'Error loading group details. Please try again.'
      }), 'error');
    } finally {
      if (!skipLoading) {
        setExpandedRowLoading(prev => ({
          ...prev,
          [groupingKey]: false
        }));
      }
    }
  }, [currentUser?.customer_id, expandedRowSorting, showToast, t]);

  // Handler for expanded table sorting changes
  const handleExpandedTableChange = useCallback((groupingKey, record) => (pagination, filters, sorter) => {
    if (sorter && sorter.order) {
      const newSortParams = {
        field: sorter.field || sorter.columnKey || 'opportunityCost',
        order: sorter.order
      };

      // Update sorting state for this specific row
      setExpandedRowSorting(prev => ({
        ...prev,
        [groupingKey]: newSortParams
      }));

      // Refetch data with new sorting (skip loading state to avoid flicker)
      // Pass sort params directly to avoid stale state closure
      fetchExpandedDetails(record, true, true, newSortParams);
    }
  }, [fetchExpandedDetails]);

  // Centralized handler for inline experiment updates with optimistic UI
  const handleExperimentUpdate = useCallback(async (experimentId, updates) => {
    if (!currentUser?.customer_id) {
      showToast(t('errorUpdatingExperiment', {
        defaultValue: 'Error updating experiment. Please try again.'
      }), 'error');
      return;
    }

    // Find which group contains this experiment
    let groupKeyToUpdate = null;
    let originalData = null;

    // Find the group that contains this experiment
    for (const [groupKey, experiments] of Object.entries(expandedRowData)) {
      const experimentIndex = experiments.findIndex(exp => exp.experimentId === experimentId);
      if (experimentIndex !== -1) {
        groupKeyToUpdate = groupKey;
        originalData = [...experiments]; // Create a copy for rollback
        break;
      }
    }

    if (!groupKeyToUpdate) {
      console.error('Could not find experiment in expanded data');
      return;
    }

    // Optimistically update the local state (skip for status changes - we'll remove the row on success)
    if (!updates.status) {
      setExpandedRowData(prev => {
        const updatedExperiments = [...prev[groupKeyToUpdate]];
        const experimentIndex = updatedExperiments.findIndex(exp => exp.experimentId === experimentId);

        if (experimentIndex !== -1) {
          // Apply the updates to the experiment
          updatedExperiments[experimentIndex] = {
            ...updatedExperiments[experimentIndex],
            ...updates,
            // Handle special cases for nested fields if needed
            ...(updates.candidateProductId && {
              selectedCandidateId: updates.candidateProductId
            }),
            ...(updates.discount !== undefined && {
              discount: updates.discount
            })
          };
        }

        return {
          ...prev,
          [groupKeyToUpdate]: updatedExperiments
        };
      });
    }

    try {
      // Get the experiment data to include price info for fallback prices
      const experiments = expandedRowData[groupKeyToUpdate] || [];
      const experiment = experiments.find(exp => exp.experimentId === experimentId);

      // Add price info when accepting experiments with fallback prices
      const updatesWithPrice = {
        ...updates,
        ...(updates.status === 'accepted' && experiment?.isFallbackPrice && experiment?.originalPrice && {
          originalPrice: experiment.originalPrice,
          isFallbackPrice: true
        })
      };

      // Call the API to persist the update
      const result = await inlineUpdateExperiment(
        currentUser.customer_id,
        experimentId,
        updatesWithPrice
      );
      // Show success message based on action
      if (updates.status === 'accepted') {
        showToast(
          t('experimentAccepted', {
            defaultValue: 'Experiment accepted successfully'
          }),
          'success'
        );
      } else if (updates.status === 'rejected') {
        showToast(
          t('experimentRejected', {
            defaultValue: 'Experiment rejected successfully'
          }),
          'success'
        );
      } else {
        showToast(
          t('experimentUpdated', {
            defaultValue: 'Experiment updated successfully'
          }),
          'success'
        );
      }

      // If status changed (accept/reject), just remove that row locally
      if (updates.status) {
        // Calculate remaining experiments and update both states atomically
        setExpandedRowData(prev => {
          const currentExperiments = prev[groupKeyToUpdate] || [];
          const remainingExperiments = currentExperiments.filter(
            exp => exp.experimentId !== experimentId
          );

          // Use queueMicrotask to update main table AFTER this state update commits
          queueMicrotask(() => {
            if (groupKeyToUpdate === UNIT_KEY) {
              return;
            }
            if (remainingExperiments.length === 0) {
              // No more experiments - collapse and remove from main table
              setExpandedRowKeys(keys => keys.filter(key => key !== groupKeyToUpdate));
              removeRow(groupKeyToUpdate);
            } else {
              // Find the removed experiment's opportunity cost
              const removedExperiment = currentExperiments.find(exp => exp.experimentId === experimentId);
              const removedCost = removedExperiment?.opportunityCost || 0;

              // Get current parent row to calculate new total
              const parentRow = data.find(row => row.groupingKey === groupKeyToUpdate);
              const newTotalCost = (parentRow?.totalOpportunityCost || 0) - removedCost;

              // Update main table count and opportunity cost (without repositioning)
              updateRowData(groupKeyToUpdate, {
                experimentCount: remainingExperiments.length,
                experimentIds: remainingExperiments.map(exp => exp.experimentId),
                totalOpportunityCost: Math.max(0, newTotalCost) // Prevent negative values
              });
            }
          });

          return {
            ...prev,
            [groupKeyToUpdate]: remainingExperiments
          };
        });

        // Vista unitaria: se pide la página de nuevo para rellenarla y cuadrar la paginación; trae
        // también los KPIs con los filtros aplicados, que refetchKPIs (sin filtros) pisaría.
        if (groupKeyToUpdate === UNIT_KEY) {
          refetch();
        } else {
          // Refetch KPIs after status change (accept/reject)
          refetchKPIs();
        }
      }

      // Mark as self-triggered to prevent refetch in event handler
      selfTriggeredUpdate.current = true;

      // Emit event for other components that might need to know
      window.dispatchEvent(new CustomEvent('experimentsUpdated', {
        detail: { experimentId, updates }
      }));

    } catch (error) {
      console.error('Error updating experiment:', error);

      // Revert the optimistic update on error
      if (originalData) {
        setExpandedRowData(prev => ({
          ...prev,
          [groupKeyToUpdate]: originalData
        }));
      }

      // Show error message
      showToast(
        getApiErrorMessage(error, t('errorUpdatingExperiment', {
          defaultValue: 'Error updating experiment. Please try again.'
        })),
        'error'
      );
    }
  }, [currentUser?.customer_id, expandedRowData, showToast, t, updateRowData, removeRow, data, refetchKPIs, refetch]);

  // Handler for draft changes (dates, candidate selection)
  const handleDraftChange = useCallback((experimentId, changes) => {
    setExperimentDrafts(prev => ({
      ...prev,
      [experimentId]: {
        ...prev[experimentId],
        ...changes,
        hasChanges: true
      }
    }));
  }, []);

  // Handler for accepting a single experiment - includes any draft changes
  const handleAcceptExperiment = useCallback(async (experimentId) => {
    const draft = experimentDrafts[experimentId] || {};
    const updates = {
      status: 'accepted',
      ...(draft.dateFrom && { dateFrom: draft.dateFrom }),
      ...(draft.dateTo && { dateTo: draft.dateTo }),
      ...(draft.candidateProductId && { candidateProductId: draft.candidateProductId }),
      ...(draft.discount !== undefined && { discount: draft.discount }),
    };

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API_BASE_URL}/experiments/pricing-preview?clientId=${currentUser.customer_id}`, { experimentId, ...updates }, { headers: createAuthHeader(token) });
      if (response.data?.pricing) setPriceReview({ experimentId, updates, pricing: response.data.pricing });
      else handleExperimentUpdate(experimentId, updates);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'No se pudieron calcular los precios.'), 'error');
    }
  }, [experimentDrafts, handleExperimentUpdate, currentUser?.customer_id, showToast]);

  // Rejecting a single experiment asks for the reason first, then clears any draft changes
  const rejectModal = useRejectionModal(async (experimentId, reason) => {
    setExperimentDrafts(prev => {
      const newDrafts = { ...prev };
      delete newDrafts[experimentId];
      return newDrafts;
    });

    await handleExperimentUpdate(experimentId, { status: 'rejected', feedback: reason });
  });
  const handleRejectExperiment = rejectModal.openRejectDialog;

  // Use centralized column configuration - conditionally based on viewMode
  const columns = useMemo(() => {
    if (viewMode === 'unit') {
      const subTableColumns = getExpandedRowColumns({
        experimentDrafts,
        onDraftChange: handleDraftChange,
        onExperimentUpdate: handleExperimentUpdate,
        onOpenAIPanel: (experimentRecord) => handleOpenAIAnalysisPanel(experimentRecord, UNIT_KEY),
        onAcceptExperiment: handleAcceptExperiment,
        onRejectExperiment: handleRejectExperiment,
        navigate,
        t,
        clientId: currentUser?.customer_id
      });
      // Los filtros de cabecera (y las columnas Categoría y Tipo) son los de la vista unitaria
      // de siempre; se toman de ahí para no duplicarlos.
      const problemColumns = createProblemsColumns(
        filteredInfo,
        unitSorter,
        filterValues,
        filtersLoading,
        navigate,
        handleOpenAIAnalysisPanel,
        t
      );
      const problemColumn = (key) => problemColumns.find(col => col.key === key);
      const withFilter = (column, filterColumn) => ({
        ...column,
        key: filterColumn.key,
        filterDropdown: filterColumn.filterDropdown,
        filteredValue: filterColumn.filteredValue
      });

      return subTableColumns.flatMap((column) => {
        if (column.key === 'machineId') {
          return [
            {
              ...withFilter(column, problemColumn('machineId')),
              sortOrder: unitSorter?.field === 'machineId' ? unitSorter.order : null
            },
            { ...problemColumn('category'), width: 130 },
            { ...problemColumn('type'), width: 140 }
          ];
        }
        if (column.key === 'productExperiment') {
          return [withFilter(column, problemColumn('product'))];
        }
        return [column];
      });
    } else {
      return getAggregatedColumns(handleAcceptAll, t);
    }
  }, [viewMode, experimentDrafts, handleDraftChange, handleExperimentUpdate, handleOpenAIAnalysisPanel, handleAcceptExperiment, handleRejectExperiment, navigate, currentUser?.customer_id, filteredInfo, unitSorter, filterValues, filtersLoading, handleAcceptAll, t]);

  // Vista unitaria: las filas de la página se piden al mismo /problems/discovery/group-details
  // que la subtabla agrupada, respetando el orden y la paginación de /problems/discovery.
  React.useEffect(() => {
    if (viewMode !== 'unit') return undefined;

    const experimentIds = data.map(row => row.experimentId).filter(Boolean);
    if (experimentIds.length === 0) {
      setExpandedRowData(prev => ({ ...prev, [UNIT_KEY]: [] }));
      return undefined;
    }

    let cancelled = false;
    setExpandedRowLoading(prev => ({ ...prev, [UNIT_KEY]: true }));
    getProblemsGroupDetails(currentUser?.customer_id, experimentIds)
      .then((rows) => {
        if (cancelled) return;
        const rowsById = new Map((rows || []).map(row => [row.experimentId, row]));
        setExpandedRowData(prev => ({
          ...prev,
          [UNIT_KEY]: data
            .map((problem) => {
              const detail = rowsById.get(problem.experimentId);
              // Categoría y Tipo se pintan como antes: con product.category y la etiqueta ya
              // traducida de problemType que manda /problems/discovery (group-details trae el código).
              return detail && { ...detail, product: problem.product, problemType: problem.problemType };
            })
            .filter(Boolean)
        }));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching unit-level experiment details:', error);
        showToast(t('errorLoadingGroupDetails', {
          defaultValue: 'Error loading group details. Please try again.'
        }), 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setExpandedRowLoading(prev => ({ ...prev, [UNIT_KEY]: false }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [viewMode, data, currentUser?.customer_id, showToast, t]);

  // Filtros y orden van al servidor para que crucen páginas; la máquina se ordena por store_id.
  const handleUnitTableChange = useCallback((pagination, filters, sorter) => {
    setFilteredInfo(filters);
    if (onFiltersChange) {
      onFiltersChange(filters, filterData, sorter);
    }
    const field = sorter?.field === 'machineAlias' ? 'machineId' : sorter?.field;
    setUnitSorter(sorter && sorter.order ? { field, order: sorter.order } : UNIT_DEFAULT_SORT);
  }, [onFiltersChange, filterData]);

  // Local table change handler (like ExperimentsTable)
  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    // Trigger server-side filtering and sorting by calling parent's onFiltersChange
    if (onFiltersChange) {
      onFiltersChange(filters, filterData, sorter);
    }
  }, [onFiltersChange, filterData]);
  
  // Local filter removal handlers
  const handleRemoveFilter = useCallback((key, value) => {
    const newFilters = { ...filteredInfo };
    if (newFilters[key]) {
      if (Array.isArray(newFilters[key])) {
        newFilters[key] = newFilters[key].filter(item => item !== value);
        if (newFilters[key].length === 0) {
          delete newFilters[key];
        }
      } else {
        delete newFilters[key];
      }
    }
    setFilteredInfo(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters, filterData, sortedInfo);
    }
  }, [filteredInfo, sortedInfo, onFiltersChange, filterData]);

  const handleClearAllFilters = useCallback(() => {
    setFilteredInfo({});
    if (onFiltersChange) {
      onFiltersChange({}, filterData, sortedInfo);
    }
  }, [sortedInfo, onFiltersChange, filterData]);

  // Handle page change
  const handlePageChange = (event, page) => {
    setPage(page);
  };

  // Handle experiment creation success
  const handleExperimentCreated = () => {
    showToast('Experiment created successfully!', 'success');
    refetch(); // Refresh data after creating experiment
  };

  // Handle row expand/collapse
  const handleExpand = useCallback((expanded, record) => {
    const { groupingKey } = record;

    if (expanded) {
      // Add to expanded keys
      setExpandedRowKeys(prev => [...prev, groupingKey]);
      // Fetch data for this row
      fetchExpandedDetails(record);
    } else {
      // Remove from expanded keys
      setExpandedRowKeys(prev => prev.filter(key => key !== groupingKey));
    }
  }, [fetchExpandedDetails]);

  // Create expandable config for aggregated view
  const expandableConfig = useMemo(() => {
    if (viewMode === 'unit') {
      return undefined;
    }

    return {
      expandedRowKeys,
      onExpand: handleExpand,
      expandedRowRender: (record) => {
        const { groupingKey } = record;
        const isLoading = expandedRowLoading[groupingKey] || false;
        const data = expandedRowData[groupingKey] || [];

        if (isLoading) {
          return (
            <Box sx={{
              p: 2,
              textAlign: 'center',
              backgroundColor: '#FFFFFF'
            }}>
              <Typography sx={{ fontSize: '13px', color: '#787774' }}>
                {t('loadingDetails', { defaultValue: 'Loading details...' })}
              </Typography>
            </Box>
          );
        }

        if (!data || data.length === 0) {
          return (
            <Box sx={{
              p: 2,
              textAlign: 'center',
              backgroundColor: '#FFFFFF'
            }}>
              <Typography sx={{ fontSize: '13px', color: '#787774' }}>
                {t('noDetailsAvailable', { defaultValue: 'No details available' })}
              </Typography>
            </Box>
          );
        }

        // Data is already sorted by the backend according to user's sort preference
        return (
          <ExpandedExperimentsTable
            data={data}
            loading={isLoading}
            experimentDrafts={experimentDrafts}
            onDraftChange={handleDraftChange}
            onExperimentUpdate={handleExperimentUpdate}
            onOpenAIPanel={(experimentRecord) => handleOpenAIAnalysisPanel(experimentRecord, groupingKey)}
            onAcceptExperiment={handleAcceptExperiment}
            onRejectExperiment={handleRejectExperiment}
            onTableChange={handleExpandedTableChange(groupingKey, record)}
            clientId={currentUser?.customer_id}
          />
        );
      },
      rowExpandable: (record) => {
        // Only allow expansion if there are experiments to show
        return record.experimentIds && record.experimentIds.length > 0;
      }
    };
  }, [viewMode, expandedRowKeys, handleExpand, expandedRowLoading, expandedRowData, experimentDrafts, t, handleDraftChange, handleExperimentUpdate, handleOpenAIAnalysisPanel, handleAcceptExperiment, handleRejectExperiment, currentUser?.customer_id]);

  return (
    <Box sx={{ 
      backgroundColor: 'white',
      borderRadius: '12px',
      border: `1px solid ${colors.border.light}`,
      overflow: 'hidden'
    }}>
      <RejectReasonDialog {...rejectModal.dialogProps} />
      <AppliedPricingDialog open={Boolean(priceReview)} pricing={priceReview?.pricing} onClose={() => setPriceReview(null)} onConfirm={(pricingOverride) => { const review = priceReview; setPriceReview(null); setExperimentDrafts(prev => { const next = { ...prev }; delete next[review.experimentId]; return next; }); handleExperimentUpdate(review.experimentId, { ...review.updates, pricingOverride }); }} />
      {/* Title Section */}
      <Box sx={{ 
        px: responsiveSpacing.sm,
        pt: responsiveSpacing.lg,
        pb: 0
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ 
              fontSize: '20px', 
              color: '#37352f',
              fontWeight: 600,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
            }}>
              {t('problems_discovery')}
            </Typography>
            {totals && (
              <>
                <Box sx={{ 
                  width: '4px', 
                  height: '4px', 
                  borderRadius: '50%', 
                  backgroundColor: '#787774',
                  alignSelf: 'center'
                }} />
                <Typography sx={{ 
                  fontSize: '16px', 
                  color: '#787774',
                  fontWeight: 500,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
                }}>
                  {totals.totalProblems} {t('problems_word')}
                </Typography>
              </>
            )}
            <Tooltip 
              title={
                <Box sx={{ p: 1 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '13px' }}>
                    {t('problemsExperiments')}
                  </Typography>
                  <Typography sx={{ mb: 1, fontSize: '12px' }}>
                    {t('discover_business_problems_description')}
                  </Typography>
                  <Typography sx={{ mb: 1, fontSize: '12px' }}>
                    <strong>{t('problem_types')}:</strong> {t('seasonal_underperformance_type')}, {t('low_turnover_type')}, {t('declining_sales_trends_type')}, {t('zero_sales_periods_type')}
                  </Typography>
                  <Typography sx={{ fontSize: '12px' }}>
                    {t('create_manual_experiments_description')}
                  </Typography>
                </Box>
              }
              placement="bottom-start"
              arrow
            >
              <InfoOutlined 
                sx={{ 
                  fontSize: '16px', 
                  color: '#8c8c8c',
                  cursor: 'help',
                  '&:hover': {
                    color: '#595959'
                  }
                }} 
              />
            </Tooltip>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* View Mode Dropdown */}
            <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 150 } }}>
              <Select
                value={viewMode}
                onChange={(e) => onViewModeChange(e.target.value)}
                sx={{
                  backgroundColor: '#F9F7F4',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #1976d2'
                  },
                  '& .MuiSelect-select': {
                    py: '6px',
                    px: '12px'
                  }
                }}
              >
                <MenuItem value="group">{t('byGroup', { defaultValue: 'By Group' })}</MenuItem>
                <MenuItem value="route">{t('byRoute', { defaultValue: 'By Route' })}</MenuItem>
                <MenuItem value="sector">{t('bySector', { defaultValue: 'By Sector' })}</MenuItem>
                <MenuItem value="unit">{t('unitLevel', { defaultValue: 'Unit Level' })}</MenuItem>
              </Select>
            </FormControl>

          </Box>
        </Box>

        {/* KPI Cards */}
        {totals && (
          <Box sx={{
            display: 'flex',
            gap: 2,
            mt: 3,
            mb: 2,
            flexWrap: 'wrap'
          }}>
            {/* Total Estimated Loss Card */}
            <KPIBox
              title={t('total_estimated_loss')}
              value={totals.totalEstimatedLoss}
              type="currency"
              icon={TrendingDown}
              color="error"
            />

            {/* Problems Without Experiments Card */}
            <KPIBox
              title={t('problems_without_experiments')}
              value={totals.totalProblems}
              type="units"
              icon={Assignment}
              color="primary"
            />

            {/* AI Accepted Experiments Card */}
            <KPIBox
              title={t('accepted_ai_experiments')}
              value={totals.acceptedAIExperiments}
              type="units"
              icon={AIIcon}
              color="info"
            />
          </Box>
        )}

        {/* Filter Pills Row with Estado Experimentos button */}
        <Box sx={{
          px: responsiveSpacing.sm,
          py: Object.keys(filteredInfo).some(key => filteredInfo[key]?.length > 0) ? 0.5 : 0.5,
          minHeight: '36px',
          backgroundColor: 'white',
          borderBottom: Object.keys(filteredInfo).some(key => filteredInfo[key]?.length > 0) ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box sx={{ flex: 1 }}>
            {Object.keys(filteredInfo).some(key => filteredInfo[key]?.length > 0) && (
              <FilterPills
                filters={filteredInfo}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleClearAllFilters}
                columnConfigs={{
                  group: { title: t('group') },
                  location: { title: t('location') },
                  machineId: { title: t('machineId') },
                  product: { title: t('producto') },
                  route: {
                    title: t('route', { defaultValue: 'Route' }),
                    valueFormatter: (value) => {
                      const routes = filterData?.routes || [];
                      const match = routes.find((route) => {
                        const routeId = route?.id ?? route?.value ?? route?.name;
                        return String(routeId) === String(value);
                      });
                      return match?.name || match?.code || value;
                    }
                  },
                  problemType: { 
                    title: t('tipo'),
                    valueFormatter: (code) => {
                      switch (code) {
                        case 'seasonal_underperformance':
                          return t('seasonal_underperformance_type');
                        case 'low_turnover':
                          return t('low_turnover_type');
                        case 'zero_sales':
                          return t('zero_sales_periods_type');
                        case 'declining_trend':
                          return t('declining_sales_trends_type');
                        // Orquestador: sin estos casos el chip y las casillas del filtro enseñan
                        // la clave cruda ('orchestrator_precio') justo al lado de las píldoras
                        // de fila, que sí vienen humanizadas del backend.
                        case 'orchestrator_precio':
                          return t('orchestrator_precio_type');
                        case 'orchestrator_reemplazo':
                          return t('orchestrator_reemplazo_type');
                        case 'orchestrator_ampliar_carril':
                          return t('orchestrator_ampliar_carril_type');
                        case 'manual_ampliar_carril':
                          return t('manual_ampliar_carril_type');
                        default:
                          return code;
                      }
                    }
                  },
                  // Some AntD configurations may report filter key as 'type' (column key)
                  type: {
                    title: t('tipo'),
                    valueFormatter: (code) => {
                      switch (code) {
                        case 'seasonal_underperformance':
                          return t('seasonal_underperformance_type');
                        case 'low_turnover':
                          return t('low_turnover_type');
                        case 'zero_sales':
                          return t('zero_sales_periods_type');
                        case 'declining_trend':
                          return t('declining_sales_trends_type');
                        // Orquestador: sin estos casos el chip y las casillas del filtro enseñan
                        // la clave cruda ('orchestrator_precio') justo al lado de las píldoras
                        // de fila, que sí vienen humanizadas del backend.
                        case 'orchestrator_precio':
                          return t('orchestrator_precio_type');
                        case 'orchestrator_reemplazo':
                          return t('orchestrator_reemplazo_type');
                        case 'orchestrator_ampliar_carril':
                          return t('orchestrator_ampliar_carril_type');
                        case 'manual_ampliar_carril':
                          return t('manual_ampliar_carril_type');
                        default:
                          return code;
                      }
                    }
                  },
                  category: { title: t('categoria') },
                  severity: { title: t('severity_filter') }
                }}
              />
            )}
          </Box>
          
          {/* Track Experiment Status Button - matching alerts table style */}
          <Box
            onClick={() => navigate('/experiments', { state: { defaultTab: 'overview' } })}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: '16px',
              py: '6px',
              cursor: 'pointer',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
              border: 'none',
              transition: 'all 0.2s ease',
              fontSize: '14px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 500,
              color: '#64748b',
              '&:hover': {
                backgroundColor: '#f1f2f4'
              }
            }}
          >
            {t('trackExperimentStatus')}
          </Box>
        </Box>
      </Box>

      {/* Content Area - Table only */}
      <Box sx={{ 
        px: responsiveSpacing.sm,
        pb: responsiveSpacing.sm
      }}>
        {/* Table View */}
        <Box sx={{
          px: responsiveSpacing.sm,
          overflowX: 'auto',
          // Visible horizontal scrollbar so the wide table is obviously swipeable on small screens.
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.28)', borderRadius: '4px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.06)' },
          '& .ant-table-content, & .ant-table-body': {
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.28)', borderRadius: '4px' },
          },
          '& .ant-table': {
            backgroundColor: 'transparent',
          },
          '& .ant-table-thead > tr > th': {
            backgroundColor: 'transparent',
            borderBottom: '1px solid #e7e5e4',
            fontSize: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 500,
            color: '#787774',
            textTransform: 'none',
            letterSpacing: '-0.01em',
            padding: '12px 8px',
          },
          // La cabecera fija (Acciones) necesita fondo: si no, se transparenta la de debajo al hacer scroll.
          '& .ant-table-thead > tr > th.ant-table-cell-fix-right': {
            backgroundColor: '#FFFFFF',
          },
          '& .ant-table-tbody > tr > td': {
            borderBottom: '1px solid #f0efee',
            padding: '12px 8px',
          },
          '& .ant-table-tbody > tr:hover > td': {
            backgroundColor: '#fbfbfa',
          },
          '& .ant-pagination': {
            display: 'none', // We'll use MUI pagination instead
          },
          // Make checkbox borders darker
          '& .ant-checkbox-inner': {
            borderColor: '#666666 !important',
          },
          '& .ant-checkbox:hover .ant-checkbox-inner': {
            borderColor: '#333333 !important',
          },
          // Make expanded row background white
          '& .ant-table-expanded-row > td': {
            backgroundColor: '#FFFFFF !important',
          },
          '& .ant-table-expanded-row-fixed': {
            backgroundColor: '#FFFFFF !important',
          }
        }}>
          <ConfigProvider locale={locale}>
            <Table
              columns={columns}
              dataSource={viewMode === 'unit' ? (expandedRowData[UNIT_KEY] || []) : data}
              rowKey={viewMode === 'unit' ? 'experimentId' : 'groupingKey'}
              loading={loading || (viewMode === 'unit' && Boolean(expandedRowLoading[UNIT_KEY]))}
              pagination={false}
              size="small"
              scroll={{ x: viewMode === 'unit' ? columns.reduce((sum, col) => sum + (col.width || 0), 0) : 1200 }}
              tableLayout="fixed"
              onChange={viewMode === 'unit' ? handleUnitTableChange : handleTableChange}
              expandable={expandableConfig}
              onRow={(record) => ({
                onMouseEnter: () => {},
                onMouseLeave: () => {},
              })}
            />
          </ConfigProvider>
        </Box>
      </Box>

      {/* Pagination */}
      {paginationInfo.totalPages > 1 && (
        <Box sx={{
          p: responsiveSpacing.md,
          display: 'flex',
          justifyContent: 'center',
          borderTop: `1px solid ${colors.border.light}`,
          backgroundColor: colors.background.surface
        }}>
          <Pagination
            count={paginationInfo.totalPages}
            page={paginationInfo.currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '13px',
                fontWeight: 500,
              }
            }}
          />
        </Box>
      )}

      {/* Toast Notifications - only show if no external showToast provided */}
      {!externalShowToast && (
        <Snackbar
          open={state.toast.toastOpen}
          autoHideDuration={4000}
          onClose={state.toast.handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={state.toast.handleToastClose}
            severity={state.toast.toastSeverity}
            variant="filled"
            sx={{
              ...theme.typography.body1
            }}
          >
            {state.toast.toastMessage}
          </Alert>
        </Snackbar>
      )}

      {/* Accept All Confirmation Modal */}
      <AcceptAllConfirmModal
        open={acceptAllModalOpen}
        onClose={() => {
          setAcceptAllModalOpen(false);
          setPendingAcceptAll(null);
        }}
        onConfirm={confirmAcceptAll}
        groupLabel={pendingAcceptAll?.groupLabel || ''}
        experimentCount={pendingAcceptAll?.experimentCount || 0}
        aggregationType={viewMode}
        loading={acceptAllLoading}
      />

    </Box>
  );
};

export default ProblemsTable;
